import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import userProfileService from "./userProfileService";
import { setAlertError, setAlertSuccess } from "../alert/alertSlice";

const initialState = {
  profile: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

export const getUserProfile = createAsyncThunk(
  "userProfile/getUserProfile",
  async (_, thunkAPI) => {
    try {
      const response = await userProfileService.getUserProfile();
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to fetch user profile";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "userProfile/updateUserProfile",
  async (profileData, thunkAPI) => {
    try {
      const response = await userProfileService.updateUserProfile(profileData);
      thunkAPI.dispatch(setAlertSuccess("User profile updated successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to update user profile";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const updateBsaNumber = createAsyncThunk(
  "userProfile/updateBsaNumber",
  async (bsaNumber, thunkAPI) => {
    try {
      const response = await userProfileService.updateBsaNumber(bsaNumber);
      thunkAPI.dispatch(setAlertSuccess("BSA number updated successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to update BSA number";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

const userProfileSlice = createSlice({
  name: "userProfile",
  initialState,
  reducers: {
    reset: (state) => {
      state.profile = null;
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.profile = action.payload;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.profile = action.payload.updated;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updateBsaNumber.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateBsaNumber.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.profile) {
          state.profile.bsa_number = action.payload.bsa_number;
        }
      })
      .addCase(updateBsaNumber.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset } = userProfileSlice.actions;

// Selectors
export const selectProfileCompletion = (state) =>
  !!(state.userProfile.profile && state.userProfile.profile.bsa_number);

export default userProfileSlice.reducer;
